import api from './api';
import { revalidate } from './mutations';

export type FooterType = 'sum' | 'avg' | 'count' | 'custom';

export interface FooterConfig {
  showFooter?: boolean;
  useFilteredData?: boolean; // If true, use filtered data; if false, use all data (default: true)
  footerColumns?: Record<string, {
    footerType: FooterType;
    footerLabel?: string;
  }>;
}

export interface TablePreset {
  id: number;
  userId: number;
  moduleName: string;
  name: string;
  description?: string;
  config: {
    visibleColumns: Record<string, boolean>;
    tableDensity: 'compact' | 'normal' | 'comfortable';
    columnWidths?: Record<string, string>;
    columnOrder?: string[];
    /** Custom display names per column key (saved with preset) */
    columnLabels?: Record<string, string>;
    sortConfig?: Array<{ key: string; direction: 'asc' | 'desc'; priority?: number }>;
    footerConfig?: FooterConfig;
  };
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreateTablePresetDto {
  moduleName: string;
  name: string;
  description?: string;
  config: {
    visibleColumns: Record<string, boolean>;
    tableDensity: 'compact' | 'normal' | 'comfortable';
    columnWidths?: Record<string, string>;
    columnOrder?: string[];
    columnLabels?: Record<string, string>;
    sortConfig?: Array<{ key: string; direction: 'asc' | 'desc'; priority?: number }>;
    footerConfig?: FooterConfig;
  };
  isDefault?: boolean;
}

export interface UpdateTablePresetDto {
  name?: string;
  description?: string;
  config?: {
    visibleColumns: Record<string, boolean>;
    tableDensity: 'compact' | 'normal' | 'comfortable';
    columnWidths?: Record<string, string>;
    columnOrder?: string[];
    columnLabels?: Record<string, string>;
    sortConfig?: Array<{ key: string; direction: 'asc' | 'desc'; priority?: number }>;
    footerConfig?: FooterConfig;
  };
  isDefault?: boolean;
}

// Get all table presets for the current user (optionally filtered by module)
export const getTablePresets = async (moduleName?: string): Promise<TablePreset[]> => {
  const params = moduleName ? `?moduleName=${encodeURIComponent(moduleName)}` : '';
  const response = await api.get(`/table-presets${params}`);
  return response.data;
};

// Get a specific table preset by ID
export const getTablePreset = async (id: number): Promise<TablePreset> => {
  const response = await api.get(`/table-presets/${id}`);
  return response.data;
};

// Get the default table preset for a module
export const getDefaultTablePreset = async (moduleName: string): Promise<TablePreset | null> => {
  try {
    const response = await api.get(`/table-presets/default/${encodeURIComponent(moduleName)}`);
    return response.data;
  } catch (error: any) {
    // If 404, return null (no default preset exists)
    if (error.response?.status === 404) {
      return null;
    }
    throw error;
  }
};

// Create a new table preset
export const createTablePreset = async (data: CreateTablePresetDto): Promise<TablePreset> => {
  const response = await api.post('/table-presets', data);
  revalidate(['table-presets', `table-presets-${data.moduleName}`]);
  return response.data;
};

// Update a table preset
export const updateTablePreset = async (id: number, data: UpdateTablePresetDto, moduleName?: string): Promise<TablePreset> => {
  const response = await api.patch(`/table-presets/${id}`, data);
  // Use moduleName from response if not provided
  const presetModuleName = moduleName || response.data?.moduleName || 'all';
  revalidate(['table-presets', `table-presets-${presetModuleName}`]);
  return response.data;
};

// Set a table preset as default
export const setDefaultTablePreset = async (id: number): Promise<TablePreset> => {
  const response = await api.patch(`/table-presets/${id}/set-default`);
  revalidate(['table-presets']);
  return response.data;
};

// Delete a table preset
export const deleteTablePreset = async (id: number, moduleName?: string): Promise<void> => {
  await api.delete(`/table-presets/${id}`);
  revalidate(['table-presets', moduleName ? `table-presets-${moduleName}` : 'table-presets']);
};

